import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, ShieldAlert, Loader2, ClipboardCheck } from 'lucide-react';
import { db } from '../Firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

export default function ProductEnquiryModal({ isOpen, onClose, productName }) {
  const [formData, setFormData] = useState({
    productName: '',
    quantity: '1',
    hospitalName: '',
    contactName: '',
    phone: '',
    email: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Dynamically update the pre-filled product name
  useEffect(() => {
    if (productName) {
      setFormData(prev => ({ ...prev, productName }));
    }
  }, [productName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, phone: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.productName || !formData.contactName || !formData.phone || !formData.hospitalName) {
      setError('Please fill in the required fields (*).');
      return;
    }

    // Phone validation: exactly 10 digits
    if (formData.phone.length !== 10) {
      setError('Phone number must be exactly 10 digits.');
      return;
    }

    // Email validation: if provided, must be valid
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setError('Please enter a valid email address.');
        return;
      }
    }

    setError('');
    setLoading(true);

    try {
      await addDoc(collection(db, 'productEnquiries'), {
        ...formData,
        submittedAt: serverTimestamp(),
        status: 'new'
      });

      // Send to FormSubmit
      try {
        await fetch("https://formsubmit.co/ajax/kirandistributors.miraj@gmail.com", {
          method: "POST",
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            _subject: "New Product Quotation Request - " + formData.productName,
            _template: "table",
            "Product Selected": formData.productName,
            "Quantity": formData.quantity,
            "Hospital / Institution": formData.hospitalName,
            "Contact Name": formData.contactName,
            "Phone Number": formData.phone,
            "Email Address": formData.email || "Not Provided",
            "Additional Notes": formData.notes || "No notes"
          })
        });
      } catch (mailErr) {
        console.error("Error sending to FormSubmit:", mailErr);
      }

      setSuccess(true);
      setFormData({
        productName: productName || '',
        quantity: '1',
        hospitalName: '',
        contactName: '',
        phone: '',
        email: '',
        notes: ''
      });
    } catch (err) {
      console.error("Error submitting product enquiry: ", err);
      setError('Enquiry submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-brand-navy/60 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-brand-silver overflow-hidden z-10 max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="bg-brand-blue p-5 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <ClipboardCheck size={22} className="text-brand-silver" />
                <div>
                  <h3 className="text-lg font-bold font-montserrat text-white">Product Quotation Request</h3>
                  <p className="text-xs text-blue-100 font-poppins">Get genuine pricing & stock availability</p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-1 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {success ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-6"
                >
                  <div className="w-16 h-16 bg-brand-green/10 text-brand-green rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                    <CheckCircle2 size={40} />
                  </div>
                  <h4 className="text-lg font-bold text-brand-navy mb-2 font-montserrat">Enquiry Received!</h4>
                  <p className="text-sm text-gray-600 font-poppins max-w-xs mx-auto mb-6">
                    Your request for <strong>{productName}</strong> has been logged. Our pharmaceutical/surgical distribution desk will contact you soon.
                  </p>
                  <button 
                    onClick={() => {
                      setSuccess(false);
                      onClose();
                    }}
                    className="btn-secondary w-full"
                  >
                    Close
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm flex items-center gap-2 rounded-r-md">
                      <ShieldAlert size={16} className="shrink-0" />
                      <p className="font-poppins">{error}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 font-poppins">Product Selected *</label>
                    <input 
                      type="text" 
                      name="productName" 
                      required
                      readOnly
                      value={formData.productName}
                      className="w-full px-3 py-2 bg-gray-50 border border-brand-silver rounded-lg focus:outline-none font-poppins text-sm font-medium text-brand-navy"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 font-poppins">Quantity *</label>
                      <input 
                        type="text" 
                        name="quantity" 
                        required
                        value={formData.quantity}
                        onChange={handleChange}
                        placeholder="e.g. 5"
                        className="w-full px-3 py-2 border border-brand-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue font-poppins text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 font-poppins">Hospital / Institution *</label>
                      <input 
                        type="text" 
                        name="hospitalName" 
                        required
                        value={formData.hospitalName}
                        onChange={handleChange}
                        placeholder="Store or Hospital Name"
                        className="w-full px-3 py-2 border border-brand-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue font-poppins text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 font-poppins">Contact Name *</label>
                    <input 
                      type="text" 
                      name="contactName" 
                      required
                      value={formData.contactName}
                      onChange={handleChange}
                      placeholder="Doctor, Admin or Owner Name"
                      className="w-full px-3 py-2 border border-brand-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue font-poppins text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 font-poppins">Phone *</label>
                      <input 
                        type="tel" 
                        name="phone" 
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="92258xxxxx"
                        className="w-full px-3 py-2 border border-brand-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue font-poppins text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 font-poppins">Email</label>
                      <input 
                        type="email" 
                        name="email" 
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="name@email.com"
                        className="w-full px-3 py-2 border border-brand-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue font-poppins text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 font-poppins">Additional Notes (Optional)</label>
                    <textarea 
                      name="notes" 
                      rows="2"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Specify size, specifications, or deadline..."
                      className="w-full px-3 py-2 border border-brand-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue font-poppins text-sm resize-none"
                    />
                  </div>

                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full btn-green justify-center"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Sending Enquiry...
                      </>
                    ) : (
                      'Request Price Details'
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
